import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Number of extra history entries to push on mount so a fast swipe-back gesture
// on mobile never exhausts the buffer in one frame.
const HISTORY_BUFFER_SIZE = 5;

const usePreventBackNavigation = (path: string) => {
  const navigate = useNavigate();

  useEffect(() => {
    // Don't apply navigation if the path is an empty string
    if (!path) return;

    // Function to push the current state into the history stack
    const pushHistory = () => {
      window.history.pushState(null, '', window.location.href);
    };

    // Function to handle the back navigation attempt
    const handlePopState = () => {
      // Re-fill the buffer immediately so subsequent swipes still have entries.
      for (let i = 0; i < HISTORY_BUFFER_SIZE; i++) {
        pushHistory();
      }
      navigate(path, { replace: true });
    };

    // Prevent the user from accidentally closing the tab (e.g. swipe-back closing
    // the tab on desktop). Not reliable on iOS Safari but kept for other browsers.
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // returnValue is still required by Chrome to trigger the confirmation dialog.
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      e.returnValue = '';
    };

    // Push a buffer of history entries upfront so even a rapid swipe-back gesture
    // on mobile cannot exhaust the stack before popstate fires.
    for (let i = 0; i < HISTORY_BUFFER_SIZE; i++) {
      pushHistory();
    }

    // Disable the native swipe-to-go-back / pull-to-refresh gesture on mobile.
    // This is the primary mechanism for preventing the browser from closing when
    // the user swipes back on iOS/Android.
    const prevOverscroll = document.documentElement.style.overscrollBehavior;
    document.documentElement.style.overscrollBehavior = 'none';

    // Listen to the popstate event (triggered by back/forward navigation)
    window.addEventListener('popstate', handlePopState);

    // Prevent tab close / refresh while on this page
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Continuously push to the history stack to prevent going back
    const interval = setInterval(pushHistory, 500);

    // Cleanup: restore original styles and remove listeners on unmount
    return () => {
      clearInterval(interval);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.documentElement.style.overscrollBehavior = prevOverscroll;
    };
  }, [navigate, path]);

  // Ensure the current state is replaced on component mount
  useEffect(() => {
    // Don't apply navigation if the path is an empty string
    if (!path) return;

    navigate(path, { replace: true });
  }, [navigate, path]);
};

export default usePreventBackNavigation;
